import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';
import envConfig from '../../../envConfig';
import { CrudType } from '../../../utility/types/CrudType';
import { CrudStatus } from '../../../utility/types/CrudStatus';

/* -----------------------------
   SELECTORS
------------------------------ */
const selectChannelList = (state) => state.channelList.channelList;

const selectChannelFromListByKey = createSelector(
  [selectChannelList, (_, channelKey) => channelKey],
  (channels, channelKey) => channels.find((channel) => channel.channelKey === channelKey),
);

const selectChannelListPageNo = createSelector(
  [(state) => state.channelList.currPage],
  (currPage) => currPage,
);

const selectChannelListTotalResults = createSelector(
  [(state) => state.channelList.totalRecords],
  (totalRecords) => totalRecords,
);

const selectAllChannelKeys = createSelector(
  [(state) => state.channelList.totalRecordsIdList],
  (totalRecordsIdList) => totalRecordsIdList,
);

const selectChannelListRowsPerPage = createSelector(
  [(state) => state.channelList],
  (channelList) => channelList.rowsPerPage,
);

const selectChannelListColumnVisibility = createSelector(
  [(state) => state.channelList],
  (channelList) => channelList.columnVisibility,
);

const selectChannelListCrudStatus = (state) => state.channelList.crudStatus;

const selectChannelListCrudType = (state) => state.channelList.crudType;

const selectActiveChannelSearchParams = createSelector(
  [(state) => state.channelList],
  (channelList) => channelList.activeSearchParams,
);

const selectChannelListHasNextPage = createSelector(
  [(state) => state.channelList],
  (channelList) => channelList.hasNextPage,
);

/* -----------------------------
   ASYNC THUNKS
------------------------------ */

const fetchFilteredChannelList = createAsyncThunk(
  'securedApi/channelList/fetchChannelList',
  async (filters, { rejectWithValue, getState }) => {
    try {
      const { rowsPerPage, currPage } = getState().channelList;
      const response = await RaaAuthenticatedRemoteService.globalInstance()
        .post(
          `${envConfig.apiUrl}/api/v1/device-data/channels/fetch-paginated-and-filtered`,
          { data: { ...filters, pageNo: currPage, pageSize: rowsPerPage } },
        );
      return { ...response, filters };
    } catch (error) {
      return rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data,
      });
    }
  },
);

const refreshFilteredChannelList = createAsyncThunk(
  'securedApi/channelList/refreshChannelList',
  async (_, { rejectWithValue, getState }) => {
    try {
      const { rowsPerPage, currPage, activeSearchParams } = getState().channelList;
      return await RaaAuthenticatedRemoteService.globalInstance()
        .post(
          `${envConfig.apiUrl}/api/v1/device-data/channels/fetch-paginated-and-filtered`,
          { data: { ...activeSearchParams, pageNo: currPage, pageSize: rowsPerPage } },
        );
    } catch (error) {
      return rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data,
      });
    }
  },
);

/* -----------------------------
   INITIAL STATE
------------------------------ */

const initialState = {
  channelList: [],
  currPage: 0,
  hasNextPage: false,
  totalRecords: 0,
  rowsPerPage: 10,
  crudOperation: CrudType.READ,
  crudStatus: CrudStatus.IDLE,
  error: {},
  activeSearchParams: {
    id: null,
    name: null,
    filters: [],
    sort: null,
    sortType: null,
  },
  columnVisibility: {
    DeviceName: true,
    SensorName: true,
    ComponentName: true,
    SensorType: true,
    SensorChannel: true,
    Port: true,
    LastReading: true,
    LastReceived: true,
    DeviceType: false,
    MACAddress: false,
    SerialNumber: false,
    Alerts: true,
  },

  totalRecordsIdList: [],
};

const channelListSlice = createSlice({
  name: 'channelList',
  initialState,
  reducers: {
    setRowsPerPage: (state, action) => {
      let rowsPerPage = action.payload;
      if (rowsPerPage > 250) rowsPerPage = 250;
      else if (rowsPerPage < 1) rowsPerPage = 1;

      state.rowsPerPage = rowsPerPage;
      state.currPage = 0;
    },

    setColumnVisibility: (state, action) => {
      const { columnName, visible } = action.payload;
      state.columnVisibility[columnName] = visible;
    },

    setChannelListPage: (state, action) => {
      const pageNo = action.payload;
      state.currPage = pageNo < 0 ? 0 : pageNo;
    },

    setAppliedChannelSearch: (state, action) => {
      state.activeSearchParams = {
        id: action.payload.id || state.activeSearchParams.id || null,
        name: action.payload.name || state.activeSearchParams.name || null,
        filters: [...action.payload.filters],
        sort: action.payload.sort || state.activeSearchParams.sort || null,
        sortType: action.payload.sortType || state.activeSearchParams.sortType || null,
      };
    },
    clearAppliedChannelSearch: (state) => {
      state.activeSearchParams = {
        // id: null,
        // name: null,
        filters: [],
        pageNo: 0,
        pageSize: 100,
        // sort: state.activeSearchParams.sort,
        // sortType: state.activeSearchParams.sortType,
      };
    },

    clearChannelListSlice: () => initialState,
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchFilteredChannelList.pending, (state) => {
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.PENDING;
      })
      .addCase(fetchFilteredChannelList.fulfilled, (state, action) => {
        state.crudStatus = CrudStatus.SUCCESS;
        state.channelList = action.payload.searchResult;
        state.currPage = action.payload.pageInfo.pageNo;
        state.rowsPerPage = action.payload.pageInfo.pageSize;
        state.totalRecords = action.payload.pageInfo.totalRecords;
        state.hasNextPage = action.payload.pageInfo.hasNext;
        state.crudStatus = CrudStatus.IDLE;
      })
      .addCase(fetchFilteredChannelList.rejected, (state, action) => {
        state.crudStatus = CrudStatus.ERROR;
        state.error = [action.payload];
      })
      .addCase(refreshFilteredChannelList.pending, (state) => {
        state.crudOperation = CrudType.BACKGROUND_READ;
        state.crudStatus = CrudStatus.PENDING;
      })
      .addCase(refreshFilteredChannelList.fulfilled, (state, action) => {
        state.crudStatus = CrudStatus.SUCCESS;
        state.channelList = action.payload.searchResult;
        state.currPage = action.payload.pageInfo.pageNo;
        state.rowsPerPage = action.payload.pageInfo.pageSize;
        state.totalRecords = action.payload.pageInfo.totalRecords;
        state.hasNextPage = action.payload.pageInfo.hasNext;
        state.crudStatus = CrudStatus.IDLE;
      })
      .addCase(refreshFilteredChannelList.rejected, (state, action) => {
        state.crudStatus = CrudStatus.ERROR;
        state.error = [action.payload];
      });
  },
});

const { actions, reducer } = channelListSlice;

export const {
  setColumnVisibility,
  setRowsPerPage,
  clearChannelListSlice,
  setChannelListPage,
  setAppliedChannelSearch,
  clearAppliedChannelSearch,
} = actions;

export {
  selectChannelListColumnVisibility,
  selectChannelListRowsPerPage,
  refreshFilteredChannelList,
  selectChannelList,
  selectChannelListPageNo,
  selectChannelListTotalResults,
  fetchFilteredChannelList,
  selectChannelListCrudStatus,
  selectActiveChannelSearchParams,
  selectChannelListHasNextPage,
  selectChannelFromListByKey,
  selectAllChannelKeys,
  selectChannelListCrudType,
};

export default reducer;
